import { CLEF_INFO, isClef, type Clef } from './clef.ts';
import { compareSpelled, parsePitchId, type Transposition } from './pitch.ts';
import type { ValidationIssue, ValidationResult } from './exercises/types.ts';

/**
 * Instrument presets. They configure the starting clef, a relevant written
 * reading range and transposition (used only for optional playback). All
 * instruments share one theory curriculum; presets only choose where it starts
 * and which notes examples use. Ranges are written pitch, clipped to what this
 * product supports (at most three ledger lines).
 */
export const INSTRUMENT_FAMILIES = ['keyboard', 'strings', 'woodwinds', 'brass', 'voice', 'percussion', 'other'] as const;
export type InstrumentFamily = (typeof INSTRUMENT_FAMILIES)[number];

export interface InstrumentPreset {
  key: string;
  name: string;
  family: InstrumentFamily;
  primaryClef: Clef;
  /** Clefs this instrument also reads; offered as additional tracks. */
  otherClefs: Clef[];
  writtenRange: { low: string; high: string };
  transposition: Transposition;
  /** Why the preset is set up this way, shown in settings. */
  rationale: string;
  /** Reads treble and bass together on a grand staff (keyboard). Sets the default staff display. */
  grandStaff?: boolean;
}

const CONCERT: Transposition = { semitones: 0, diatonic: 0 };

export const INSTRUMENT_PRESETS: readonly InstrumentPreset[] = [
  {
    key: 'piano',
    name: 'Piano / keyboard',
    family: 'keyboard',
    primaryClef: 'treble',
    otherClefs: ['bass'],
    writtenRange: { low: 'C4', high: 'C6' },
    transposition: CONCERT,
    rationale: 'Piano reads treble clef for the right hand and bass clef for the left. Your path starts with treble clef (add a bass-clef path any time in Settings), and practice uses the grand staff.',
    grandStaff: true,
  },
  {
    key: 'violin',
    name: 'Violin',
    family: 'strings',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'G3', high: 'E6' },
    transposition: CONCERT,
    rationale: 'Violin music is written in treble clef; its lowest open string is G3.',
  },
  {
    key: 'viola',
    name: 'Viola',
    family: 'strings',
    primaryClef: 'alto',
    otherClefs: ['treble'],
    writtenRange: { low: 'C3', high: 'G5' },
    transposition: CONCERT,
    rationale: 'Viola reads alto clef, switching to treble for high passages; its lowest string is C3.',
  },
  {
    key: 'cello',
    name: 'Cello',
    family: 'strings',
    primaryClef: 'bass',
    otherClefs: ['tenor', 'treble'],
    writtenRange: { low: 'C2', high: 'A4' },
    transposition: CONCERT,
    rationale: 'Cello reads bass clef, with tenor clef for higher passages; its lowest string is C2.',
  },
  {
    key: 'double-bass',
    name: 'Double bass',
    family: 'strings',
    primaryClef: 'bass',
    otherClefs: ['tenor'],
    writtenRange: { low: 'G1', high: 'D4' },
    transposition: { semitones: -12, diatonic: -7 },
    rationale: 'Double bass is written in bass clef an octave above where it sounds. Written E1 needs four ledger lines, so practice starts at G1.',
  },
  {
    key: 'guitar',
    name: 'Guitar',
    family: 'strings',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'E3', high: 'E6' },
    transposition: { semitones: -12, diatonic: -7 },
    rationale: 'Guitar is written in treble clef an octave above where it sounds; the low E string is written E3.',
  },
  {
    key: 'flute',
    name: 'Flute',
    family: 'woodwinds',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'C4', high: 'F6' },
    transposition: CONCERT,
    rationale: 'Flute reads treble clef from middle C upward.',
  },
  {
    key: 'oboe',
    name: 'Oboe',
    family: 'woodwinds',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'B3', high: 'F6' },
    transposition: CONCERT,
    rationale: 'Oboe reads treble clef; its lowest note is B♭3.',
  },
  {
    key: 'clarinet-bb',
    name: 'Clarinet in B♭',
    family: 'woodwinds',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'E3', high: 'F6' },
    transposition: { semitones: -2, diatonic: -1 },
    rationale: 'B♭ clarinet reads treble clef and sounds a major second below written pitch. Note reading always uses written notes.',
  },
  {
    key: 'alto-sax',
    name: 'Alto saxophone',
    family: 'woodwinds',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'B3', high: 'F6' },
    transposition: { semitones: -9, diatonic: -5 },
    rationale: 'E♭ alto saxophone reads treble clef and sounds a major sixth below written pitch.',
  },
  {
    key: 'tenor-sax',
    name: 'Tenor saxophone',
    family: 'woodwinds',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'B3', high: 'F6' },
    transposition: { semitones: -14, diatonic: -8 },
    rationale: 'B♭ tenor saxophone reads treble clef and sounds a major ninth below written pitch.',
  },
  {
    key: 'bassoon',
    name: 'Bassoon',
    family: 'woodwinds',
    primaryClef: 'bass',
    otherClefs: ['tenor'],
    writtenRange: { low: 'G1', high: 'E4' },
    transposition: CONCERT,
    rationale: 'Bassoon reads bass clef and tenor clef for its upper register.',
  },
  {
    key: 'trumpet-bb',
    name: 'Trumpet in B♭',
    family: 'brass',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'F3', high: 'C6' },
    transposition: { semitones: -2, diatonic: -1 },
    rationale: 'B♭ trumpet reads treble clef and sounds a major second below written pitch.',
  },
  {
    key: 'horn-f',
    name: 'Horn in F',
    family: 'brass',
    primaryClef: 'treble',
    otherClefs: ['bass'],
    writtenRange: { low: 'F3', high: 'C6' },
    transposition: { semitones: -7, diatonic: -4 },
    rationale: 'Horn in F reads mostly treble clef, sounding a perfect fifth below written pitch; low parts use bass clef.',
  },
  {
    key: 'trombone',
    name: 'Trombone',
    family: 'brass',
    primaryClef: 'bass',
    otherClefs: ['tenor'],
    writtenRange: { low: 'E2', high: 'F4' },
    transposition: CONCERT,
    rationale: 'Trombone reads bass clef, with tenor clef for higher parts.',
  },
  {
    key: 'euphonium',
    name: 'Euphonium / baritone',
    family: 'brass',
    primaryClef: 'bass',
    otherClefs: [],
    writtenRange: { low: 'E2', high: 'A4' },
    transposition: CONCERT,
    rationale: 'Bass-clef euphonium and baritone parts are written at concert pitch.',
  },
  {
    key: 'tuba',
    name: 'Tuba',
    family: 'brass',
    primaryClef: 'bass',
    otherClefs: [],
    writtenRange: { low: 'G1', high: 'C4' },
    transposition: CONCERT,
    rationale: 'Tuba reads bass clef well below the staff.',
  },
  {
    key: 'voice-high',
    name: 'Voice — soprano / alto',
    family: 'voice',
    primaryClef: 'treble',
    otherClefs: [],
    writtenRange: { low: 'G3', high: 'C6' },
    transposition: CONCERT,
    rationale: 'Soprano and alto parts are written in treble clef.',
  },
  {
    key: 'voice-tenor',
    name: 'Voice — tenor',
    family: 'voice',
    primaryClef: 'treble',
    otherClefs: ['bass'],
    writtenRange: { low: 'C4', high: 'C6' },
    transposition: { semitones: -12, diatonic: -7 },
    rationale: 'Tenor parts are usually written in treble clef an octave above where they sound.',
  },
  {
    key: 'voice-low',
    name: 'Voice — baritone / bass',
    family: 'voice',
    primaryClef: 'bass',
    otherClefs: [],
    writtenRange: { low: 'E2', high: 'F4' },
    transposition: CONCERT,
    rationale: 'Baritone and bass parts are written in bass clef.',
  },
  {
    key: 'mallets',
    name: 'Mallet percussion',
    family: 'percussion',
    primaryClef: 'treble',
    otherClefs: ['bass'],
    writtenRange: { low: 'C4', high: 'C6' },
    transposition: CONCERT,
    rationale: 'Xylophone, glockenspiel and vibraphone parts are written in treble clef; marimba adds bass clef.',
  },
];

export const INSTRUMENT_BY_KEY: ReadonlyMap<string, InstrumentPreset> = new Map(INSTRUMENT_PRESETS.map((i) => [i.key, i]));

export const CUSTOM_INSTRUMENT_KEY = 'custom';

export interface InstrumentProfileInput {
  instrumentKey: string;
  label: string;
  primaryClef: Clef;
  rangeLow: string;
  rangeHigh: string;
  transpositionSemitones: number;
}

export function presetToProfile(preset: InstrumentPreset): InstrumentProfileInput {
  return {
    instrumentKey: preset.key,
    label: preset.name,
    primaryClef: preset.primaryClef,
    rangeLow: preset.writtenRange.low,
    rangeHigh: preset.writtenRange.high,
    transpositionSemitones: preset.transposition.semitones,
  };
}

export function validateInstrumentProfile(input: InstrumentProfileInput): ValidationResult<InstrumentProfileInput> {
  const issues: ValidationIssue[] = [];
  if (input.instrumentKey !== CUSTOM_INSTRUMENT_KEY && !INSTRUMENT_BY_KEY.has(input.instrumentKey)) {
    issues.push({ path: 'instrumentKey', code: 'unknown_instrument', message: 'Choose an instrument or “Custom”.' });
  }
  const label = input.label.trim();
  if (label.length < 1 || label.length > 60) {
    issues.push({ path: 'label', code: 'invalid_label', message: 'Instrument name must be 1–60 characters.' });
  }
  if (!isClef(input.primaryClef)) {
    issues.push({ path: 'primaryClef', code: 'invalid_clef', message: 'Choose a clef.' });
  }
  const low = parsePitchId(input.rangeLow);
  const high = parsePitchId(input.rangeHigh);
  if (!low) issues.push({ path: 'rangeLow', code: 'invalid_pitch', message: 'Choose a valid lowest note.' });
  if (!high) issues.push({ path: 'rangeHigh', code: 'invalid_pitch', message: 'Choose a valid highest note.' });
  if (low && high && isClef(input.primaryClef)) {
    const practical = CLEF_INFO[input.primaryClef].practicalRange;
    if (compareSpelled(low, high) >= 0) {
      issues.push({ path: 'rangeHigh', code: 'range_inverted', message: 'The highest note must be above the lowest note.' });
    }
    if (compareSpelled(low, practical.low) < 0 || compareSpelled(high, practical.high) > 0) {
      issues.push({
        path: 'rangeLow',
        code: 'out_of_clef_range',
        message: 'Range must fit within three ledger lines of the chosen clef.',
      });
    }
  }
  if (!Number.isInteger(input.transpositionSemitones) || Math.abs(input.transpositionSemitones) > 24) {
    issues.push({ path: 'transpositionSemitones', code: 'invalid_transposition', message: 'Transposition must be between −24 and 24 semitones.' });
  }
  return issues.length ? { ok: false, issues } : { ok: true, value: { ...input, label } };
}

/** Whether an instrument normally reads from a grand staff (the default staff display for it). */
export function readsGrandStaff(instrumentKey: string | null | undefined): boolean {
  return Boolean(instrumentKey && INSTRUMENT_PRESETS.find((preset) => preset.key === instrumentKey)?.grandStaff);
}
